import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Acorn, ArrowRight, Binoculars, Bird, Bug, Butterfly, Camera, Drop, Fish, Globe,
  HandHeart, Leaf, MagnifyingGlass, MapPin, PawPrint, Plant, SealCheck, ShieldWarning,
  Sparkle, Tree, Users, Virus,
} from "@phosphor-icons/react";
import { http, API_BASE } from "@/lib/api";
import SeoMeta from "@/components/SeoMeta";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import PageGuide from "@/components/PageGuide";
import GuidePdfDownload from "@/components/GuidePdfDownload";
import RelatedReading from "@/components/RelatedReading";

function abs(u) { return !u ? "" : u.startsWith("http") ? u : `${API_BASE}${u}`; }

const GROUP_ICONS = { mammals: PawPrint, birds: Bird, insects: Butterfly, reptiles: Bug, amphibians: Drop, "fish-marine": Fish, fungi: Acorn, other: Virus };

const FEATURES = [
  { icon: Camera, title: "AI-Powered Identification", copy: "Visual AI together with trusted global biodiversity databases — candidates, never verdicts." },
  { icon: Globe, title: "Global Knowledge", copy: "Taxonomy and occurrence data from iNaturalist, Wikipedia and the NaturePulse catalogue." },
  { icon: MapPin, title: "See It Around You", copy: "Explore which species are being observed in your area and around the world." },
  { icon: Users, title: "Community Science", copy: "Share your observations and help build a living record of Earth's biodiversity." },
];

const LADDER = [
  ["AI suggested", "Visual AI proposes candidates — never a verdict"],
  ["Probable", "High-confidence match, not yet database-checked"],
  ["Database matched", "Validated against open taxonomic records"],
  ["Community confirmed", "Other observers saw and confirmed it"],
  ["Expert verified", "A qualified steward signed off"],
];

export default function SpeciesPulseHome() {
  const [data, setData] = useState(null);
  const [nearby, setNearby] = useState(null); // null = not tried
  const [locState, setLocState] = useState("idle"); // idle | locating | denied
  const fileRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    http.get("/speciespulse/home").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  const onPickPhoto = (files) => {
    const f = files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => {
      try { sessionStorage.setItem("np_identify_prefill", r.result); } catch {}
      navigate("/identify");
    };
    r.readAsDataURL(f);
  };

  const findNearMe = () => {
    if (!navigator.geolocation) { setLocState("denied"); return; }
    setLocState("locating");
    navigator.geolocation.getCurrentPosition(
      async (p) => {
        try {
          const { data } = await http.get("/nature/observations/nearby", {
            params: { lat: p.coords.latitude, lon: p.coords.longitude, radius_km: 100, limit: 12 },
          });
          const speciesCats = new Set(["mammals", "birds", "insects", "reptiles", "amphibians", "fish", "aquatic", "fungi"]);
          setNearby((data.observations || []).filter((o) => speciesCats.has(o.category)));
        } catch { setNearby([]); }
        setLocState("idle");
      },
      () => setLocState("denied"),
      { timeout: 10000 },
    );
  };

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const groups = data?.groups || [];
  const hero = data?.art?.hero;
  const featured = data?.featured || [];
  const recent = data?.recent_observations || [];
  const obsCards = nearby ?? recent;

  return (
    <main className="min-h-screen bg-white text-[#14532D]" data-testid="speciespulse-home">
      <SeoMeta
        title="SpeciesPulse — Discover the Living World Around You"
        description="Photograph a species. Identify it with AI and trusted biodiversity data. Learn where it lives, document your observation and help build a living record of Earth's biodiversity."
        canonical={`${origin}/speciespulse`}
        jsonLd={{ "@context": "https://schema.org", "@type": "CollectionPage", name: "SpeciesPulse", url: `${origin}/speciespulse` }}
      />

      {/* ---------------- HERO ---------------- */}
      <section className="relative overflow-hidden pt-32 md:pt-36" data-testid="speciespulse-hero">
        {hero && (
          <div className="absolute inset-0">
            <img src={abs(hero)} alt="Wildlife of Earth — elephant, deer, frog, sea turtle, kingfisher, butterfly and fungi" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/20" />
          </div>
        )}
        <div className="relative max-w-[1200px] mx-auto px-5 md:px-10 pb-14">
          <div className="grid gap-10 lg:grid-cols-[1.25fr_1fr] items-center">
            <div>
              <div className="flex items-center gap-2">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#166534] text-white">
                  <MagnifyingGlass size={22} weight="bold" />
                </span>
                <Leaf size={26} weight="fill" className="text-[#65A30D] -ml-4 mt-3" />
              </div>
              <h1 className="mt-3 font-serif-display text-5xl md:text-7xl leading-[0.98] tracking-tight text-[#166534]" data-testid="speciespulse-title">
                Species<span className="text-[#65A30D]">Pulse</span>
              </h1>
              <div className="mt-2 font-mono-tech text-[11px] tracking-[0.3em] uppercase text-[#3F6212]" data-testid="speciespulse-tagline">
                Identify · Explore · Learn · Protect
              </div>
              <p className="mt-4 max-w-xl text-base md:text-lg text-[#1F2937] leading-relaxed" data-testid="speciespulse-intro">
                Discover the incredible diversity of life on Earth. Upload a photo and let AI, global
                databases and our community help you identify animals, birds, insects, reptiles,
                amphibians, fish, fungi and more.
              </p>
              <ListenTranslateBar text="SpeciesPulse. Discover the living world around you. Photograph a species, identify it with AI and trusted biodiversity data, and help build a living record of Earth's biodiversity." testidPrefix="speciespulse-tools" />
              <div className="mt-6"><PageGuide pageKey="species:home" testidPrefix="speciespulse" /></div>
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <button onClick={() => fileRef.current?.click()} data-testid="speciespulse-start-identifying"
                  className="inline-flex items-center gap-2 rounded-full bg-[#166534] px-7 py-3.5 font-mono-tech text-[11px] tracking-[0.26em] uppercase text-white hover:bg-[#14532D] transition-colors">
                  Start Identifying <ArrowRight size={14} weight="bold" />
                </button>
                <GuidePdfDownload url={`${API_BASE}/api/speciespulse/field-guide.pdf`}
                  label="Kids field guide (PDF)" testid="speciespulse-field-guide-all" big />
                <Link to="/passport" data-testid="speciespulse-passport-link"
                  className="inline-flex items-center gap-2 rounded-full border border-[#B45309]/30 bg-[#FFFBEB] px-6 py-3.5 font-mono-tech text-[11px] tracking-[0.26em] uppercase text-[#B45309] hover:bg-[#FEF3C7] transition-colors">
                  <SealCheck size={14} weight="bold" /> My nature passport
                </Link>
                <span className="font-serif-display italic text-[#3F6212] text-lg leading-tight">
                  Every species matters.<br />Together we can protect them.
                </span>
              </div>
              {data?.stats && (
                <div className="mt-5 flex flex-wrap gap-4 font-mono-tech text-[10px] tracking-[0.2em] uppercase text-[#3F6212]/80" data-testid="speciespulse-stats">
                  <span>{data.stats.species_documented} species documented</span>
                  <span>·</span>
                  <span>{data.stats.species_observations} community observations</span>
                </div>
              )}
            </div>

            {/* What species is this? */}
            <div className="rounded-3xl bg-white/95 backdrop-blur border border-black/5 shadow-xl p-6" data-testid="speciespulse-upload-card">
              <h2 className="font-serif-display text-2xl text-[#14532D]">What species is this?</h2>
              <p className="mt-1 text-sm text-[#374151]">Upload a photo and let NaturePulse identify it.</p>
              <div
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); onPickPhoto(e.dataTransfer.files); }}
                className="mt-4 cursor-pointer rounded-2xl border-2 border-dashed border-[#166534]/25 hover:border-[#166534]/60 transition-colors p-8 text-center"
                data-testid="speciespulse-dropzone"
              >
                <Camera size={30} weight="bold" className="mx-auto text-[#166534]" />
                <div className="mt-2 font-medium text-[#14532D]">Upload a Photo</div>
                <div className="mt-0.5 text-xs text-[#6B7280]">Drag &amp; drop or click to choose</div>
              </div>
              <input ref={fileRef} type="file" accept="image/*" hidden data-testid="speciespulse-file-input"
                onChange={(e) => { onPickPhoto(e.target.files); e.target.value = ""; }} />
              <button onClick={() => fileRef.current?.click()} data-testid="speciespulse-identify-btn"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#166534] to-[#65A30D] px-6 py-3 font-medium text-white hover:opacity-95">
                Identify Species <Sparkle size={15} weight="fill" />
              </button>
              <Link to="/identify" className="mt-3 block text-center text-sm text-[#166534] hover:underline" data-testid="speciespulse-how-it-works">
                How it works ›
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- CATEGORY CIRCLES ---------------- */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-10 mt-10" data-testid="speciespulse-categories">
        <div className="flex flex-wrap items-end justify-center gap-x-5 gap-y-6">
          <div className="w-24 text-center">
            <Link to="/plants" data-testid="speciespulse-cat-plants" className="group block">
              <CircleImg src={abs(data?.art?.["circle-plants"])} icon={Plant} tint="#65A30D" />
              <div className="mt-2 text-sm font-medium text-[#14532D] group-hover:text-[#166534]">Plants</div>
            </Link>
            <Link to="/read/articles/species-button-plants" data-testid="speciespulse-cat-guide-plants"
              className="mt-0.5 inline-block font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[#B85F2A] hover:text-[#a25526]">
              AI guide →
            </Link>
          </div>
          {groups.map((g) => {
            const Icon = GROUP_ICONS[g.slug] || Bug;
            return (
              <div key={g.slug} className="w-24 text-center">
                <Link to={`/speciespulse/group/${g.slug}`} data-testid={`speciespulse-cat-${g.slug}`} className="group block">
                  <CircleImg src={abs(g.art)} icon={Icon} tint="#166534" count={g.species_count} />
                  <div className="mt-2 text-sm font-medium text-[#14532D] group-hover:text-[#166534] leading-tight">{g.label}</div>
                </Link>
                <Link to={`/read/articles/species-button-${g.slug}`} data-testid={`speciespulse-cat-guide-${g.slug}`}
                  className="mt-0.5 inline-block font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[#B85F2A] hover:text-[#a25526]">
                  AI guide →
                </Link>
              </div>
            );
          })}
          <Link to="/explore" data-testid="speciespulse-cat-all" className="group w-24 text-center">
            <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#65A30D] text-white text-2xl font-bold group-hover:bg-[#166534] transition-colors">···</span>
            <div className="mt-2 text-sm font-medium text-[#14532D] group-hover:text-[#166534]">All Categories</div>
          </Link>
        </div>
        <p className="mt-4 text-center text-xs text-[#6B7280]">
          Plants, trees, flowers and crops live in <Link to="/plants" className="underline text-[#166534]">Plant Pulse</Link> — our dedicated botanical atlas.
        </p>
      </section>

      {/* ---------------- QUICK FEATURES ---------------- */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-10 mt-14" data-testid="speciespulse-features">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f) => (
            <div key={f.title} className="rounded-2xl border border-black/5 bg-white p-5 shadow-sm">
              <f.icon size={26} weight="fill" className="text-[#166534]" />
              <div className="mt-2 font-semibold text-[#14532D]">{f.title}</div>
              <p className="mt-1 text-sm text-[#4B5563] leading-relaxed">{f.copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- FEATURED + HEALTHIER PLANET ---------------- */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-10 mt-14" data-testid="speciespulse-featured">
        <div className="grid gap-6 lg:grid-cols-[1fr_1.6fr]">
          <div className="rounded-3xl bg-[#F0FDF4] border border-[#166534]/10 p-7 flex flex-col justify-center">
            <div className="font-serif-display italic text-[#3F6212] text-lg">Observe · Share · Learn · Protect</div>
            <h2 className="mt-2 font-serif-display text-3xl text-[#14532D]">A Healthier Planet for Future Generations</h2>
            <p className="mt-3 text-sm text-[#374151] leading-relaxed">
              By learning about the amazing species around us, we build a deeper appreciation for
              nature and a stronger commitment to protect it.
            </p>
            <Link to="/challenges" data-testid="speciespulse-join-movement"
              className="mt-5 inline-flex w-fit items-center gap-2 rounded-full bg-[#166534] px-6 py-3 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-white hover:bg-[#14532D]">
              Join the Movement <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <h2 className="font-serif-display text-2xl md:text-3xl text-[#14532D]">Featured Species</h2>
              <Link to="/explore/fauna" className="text-sm text-[#166534] hover:underline" data-testid="speciespulse-featured-more">See More ›</Link>
            </div>
            <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
              {featured.map((s) => (
                <Link key={s.slug} to={`/speciespulse/species/${s.slug}`} data-testid={`speciespulse-featured-${s.slug}`}
                  className="group rounded-2xl border border-black/5 bg-white overflow-hidden shadow-sm hover:shadow-md hover:border-[#166534]/30 transition-all">
                  <FeaturedImage slug={s.slug} title={s.title} />
                  <div className="p-3">
                    <div className="font-semibold text-sm text-[#14532D] leading-tight">{s.title}</div>
                    {s.scientific_name && <div className="mt-0.5 text-xs italic text-[#6B7280]">{s.scientific_name}</div>}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- NEAR YOU / RECENT ---------------- */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-10 mt-14" data-testid="speciespulse-near-me">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-serif-display text-2xl md:text-3xl text-[#14532D]">
            {nearby ? "Species near you" : "Recent species observations"}
          </h2>
          <button onClick={findNearMe} disabled={locState === "locating"} data-testid="speciespulse-near-me-btn"
            className="inline-flex items-center gap-2 rounded-full border border-[#166534]/30 px-4 py-2 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[#166534] hover:bg-[#166534] hover:text-white transition-colors disabled:opacity-50">
            <MapPin size={13} weight="bold" /> {locState === "locating" ? "Locating…" : nearby ? "Refresh" : "Use my location"}
          </button>
        </div>
        {locState === "denied" && (
          <p className="mt-2 text-sm text-[#B45309]" data-testid="speciespulse-near-me-denied">
            Location unavailable — showing recent observations from the network instead.
          </p>
        )}
        {obsCards.length === 0 ? (
          <p className="mt-4 text-sm text-[#4B5563]">
            No species observations {nearby ? "within 100 km yet — yours could be the first" : "yet"}. Photograph a species and identify it to start the record.
          </p>
        ) : (
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {obsCards.slice(0, 8).map((o) => (
              <Link key={o.id} to={`/observations/${o.id}`} data-testid={`speciespulse-obs-${o.id}`}
                className="rounded-2xl border border-black/5 bg-white overflow-hidden shadow-sm hover:border-[#166534]/40 transition-colors">
                {o.media?.[0]?.url && (
                  <img src={abs(o.media[0].url)} alt={o.title} className="h-28 w-full object-cover" loading="lazy" />
                )}
                <div className="p-3">
                  <div className="font-medium text-sm text-[#14532D] leading-tight">{o.title || "Species observation"}</div>
                  <div className="mt-1 font-mono-tech text-[9px] tracking-[0.16em] uppercase text-[#6B7280]">
                    {o.date}{o.location?.city || o.location?.display_name ? ` · ${o.location.city || o.location.display_name}` : ""}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* ---------------- TRUST LADDER ---------------- */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-10 mt-14" data-testid="speciespulse-trust">
        <div className="rounded-3xl bg-[#E9F3EA] border border-[#166534]/10 p-6 md:p-8">
          <div className="flex items-center gap-2 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#166534]">
            <SealCheck size={14} weight="fill" /> How identification earns trust
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-5">
            {LADDER.map(([label, desc]) => (
              <div key={label} className="rounded-2xl bg-white border border-black/8 p-3 shadow-sm">
                <div className="font-semibold text-[#14532D]">{label}</div>
                <div className="mt-1 text-xs text-[#5B4A38] leading-snug">{desc}</div>
              </div>
            ))}
          </div>
          <div className="mt-5 flex flex-wrap gap-x-8 gap-y-2 text-xs text-[#4A3B2C]">
            <span className="inline-flex items-center gap-1.5"><ShieldWarning size={14} weight="fill" className="text-[#B45309]" />
              A wild mushroom or plant is never declared safe to eat from a photo.</span>
            <span className="inline-flex items-center gap-1.5"><Binoculars size={14} weight="fill" className="text-[#166534]" />
              Keep distance from wildlife — never handle, feed or disturb animals for a photo.</span>
            <span className="inline-flex items-center gap-1.5"><Tree size={14} weight="fill" className="text-[#166534]" />
              Threatened-species coordinates are automatically obscured.</span>
          </div>
        </div>
      </section>

      {/* ---------------- LEARN / PROTECT / CONTRIBUTE ---------------- */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-10 mt-14 pb-24" data-testid="speciespulse-contribute">
        <div className="grid gap-4 md:grid-cols-3">
          {[
            { icon: Globe, t: "Explore the map", d: "See where species are being observed — from your neighbourhood to the far side of the planet.", to: "/speciespulse/map", cta: "Open the map", tid: "speciespulse-map-cta" },
            { icon: HandHeart, t: "Protect nature", d: "Conservation status comes from authoritative sources — learn which species need help and why.", to: "/explore/fauna", cta: "Browse the catalogue", tid: "speciespulse-protect-cta" },
            { icon: Camera, t: "Contribute", d: "Every observation becomes a permanent data point in a decades-long record of life on Earth.", to: "/identify", cta: "Identify & contribute", tid: "speciespulse-contribute-cta" },
          ].map((c) => (
            <div key={c.t} className="rounded-3xl border border-black/5 bg-white p-6 shadow-sm">
              <c.icon size={26} weight="fill" className="text-[#166534]" />
              <div className="mt-2 font-serif-display text-2xl text-[#14532D]">{c.t}</div>
              <p className="mt-2 text-sm text-[#4B5563] leading-relaxed">{c.d}</p>
              <Link to={c.to} data-testid={c.tid}
                className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#166534]/30 px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[#166534] hover:bg-[#166534] hover:text-white transition-colors">
                {c.cta} <ArrowRight size={11} weight="bold" />
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-5 md:px-10 mt-10">
        <RelatedReading tag="species-pulse" testid="speciespulse-related" />
      </section>
    </main>
  );
}

function CircleImg({ src, icon: Icon, tint, count }) {
  const [err, setErr] = useState(false);
  if (src && !err) {
    return (
      <span className="relative mx-auto block h-20 w-20">
        <img src={src} alt="" onError={() => setErr(true)} loading="lazy"
          className="h-20 w-20 rounded-full object-cover border-2 border-[#166534]/15 shadow-sm group-hover:border-[#166534]/50 transition-colors" />
        {count > 0 && (
          <span className="absolute -bottom-1 -right-1 rounded-full bg-[#166534] px-1.5 py-0.5 font-mono-tech text-[9px] text-white">{count}</span>
        )}
      </span>
    );
  }
  return (
    <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border-2 border-black/5 shadow-sm" style={{ backgroundColor: `${tint}18` }}>
      <Icon size={30} weight="fill" style={{ color: tint }} />
    </span>
  );
}

/* Featured species tiles use the AI category art that matches each species'
   group — no external hotlinks, all generated in-house. */
const FEATURED_ART = {
  "monarch-butterfly": "circle-insects",
  "red-eyed-tree-frog": "circle-amphibians",
  "bald-eagle": "circle-birds",
  "green-sea-turtle": "circle-fish-marine",
};

function FeaturedImage({ slug, title }) {
  const [art, setArt] = useState(null);
  useEffect(() => {
    http.get("/docket-art/speciespulse").then(({ data }) => setArt(data.art || {})).catch(() => {});
  }, []);
  const key = FEATURED_ART[slug];
  const src = key && art?.[key] ? abs(art[key]) : null;
  return src ? (
    <img src={src} alt={title} className="h-28 w-full object-cover" loading="lazy" />
  ) : (
    <div className="h-28 w-full bg-[#F0FDF4] flex items-center justify-center">
      <Leaf size={28} weight="fill" className="text-[#166534]/40" />
    </div>
  );
}
